<script setup>
import { computed, ref, watch } from 'vue';
import { useRoadmapsStore } from '@/stores/roadmaps';
import { useDemoEvents, resetDemoEvents } from '@/composables/useDemoEvents';
import { openProject } from '@/composables/useUi';
import { phaseVars } from '@/lib/palette';
import { totalSlots } from '@/lib/schema';
import { hasDates, rangeDates, formatDateRange } from '@/lib/dates';
import RoadmapBoard from '@/components/roadmap/RoadmapBoard.vue';
import CapacityView from '@/components/roadmap/CapacityView.vue';
import Segmented from '@/components/ui/Segmented.vue';
import AppButton from '@/components/ui/AppButton.vue';
import Icon from '@/components/ui/Icon.vue';
import CoverageChart from './CoverageChart.vue';
import TourCallout from './TourCallout.vue';
import logo from '@/assets/img/logo-light.svg';

const props = defineProps({
  preview: { type: Boolean, default: false }
});

const STEPS = [
  { goal: 'move', title: 'Drag a block to move it.', hint: 'Grab the CSV export block and slide it along the sprints.' },
  { goal: 'resize', title: 'Pull an edge to stretch it.', hint: 'Drag the left or right edge of any block to change how long it runs.' },
  { goal: 'create', title: 'Draw a new block.', hint: 'Drag across an empty part of any row. A new block appears where you let go.' },
  { goal: 'phase', title: 'Click a block to change its phase.', hint: 'A single click opens the block editor. Pick a different phase.' },
  { goal: 'undo', title: 'Changed your mind? Undo it.', hint: 'Press Undo in the header, or Ctrl+Z (Cmd+Z on a Mac). Every edit can be undone and redone.' },
  { goal: 'half', title: 'Plan in half sprints.', hint: 'Switch on half sprints in the toolbar so blocks can start and finish mid-sprint.' },
  { goal: 'capacity', title: 'See who is stretched.', hint: 'Open Capacity for phase load and how busy each person is, sprint by sprint.' },
  { goal: 'compare', title: 'Lay roadmaps over each other.', hint: 'Open Compare to find gaps and anyone booked on two roadmaps at once.' }
];

const store = useRoadmapsStore();
const feed = useDemoEvents();

const activeId = ref('demo-q3-platform');
const view = ref('board');
const visited = ref(new Set());
const step = ref(0);
const celebrating = ref(false);
const minimised = ref(false);
let timer = null;

const roadmap = computed(() => store.byId(activeId.value) || store.sorted[0]);
const half = computed(() => !!roadmap.value?.settings.halfSprints);

const views = [
  { value: 'board', label: 'Board', icon: 'board' },
  { value: 'capacity', label: 'Capacity', icon: 'capacity' },
  { value: 'compare', label: 'Compare', icon: 'layers' }
];

const summary = computed(() => {
  if (view.value === 'compare') return `${store.sorted.map((r) => r.name).join(' and ')}, lined up by date`;
  const rm = roadmap.value;
  const range = hasDates(rm) ? `, ${formatDateRange(rangeDates(rm.settings, 0, totalSlots(rm)))}` : '';
  return `${rm.projects.length} projects, ${rm.members.length} people, ${rm.settings.sprintCount} sprints${rm.settings.halfSprints ? ', half sprints on' : ''}${range}`;
});

const canUndo = computed(() => store.canUndo(roadmap.value?.id));
const canRedo = computed(() => store.canRedo(roadmap.value?.id));

function undo() {
  if (store.undo(roadmap.value.id)) visited.value = new Set([...visited.value, 'undo']);
}

function redo() {
  store.redo(roadmap.value.id);
}

function onKey(e) {
  if (props.preview || !(e.metaKey || e.ctrlKey)) return;
  if (/^(input|textarea|select)$/i.test(e.target?.tagName || '')) return;
  if (document.querySelector('[aria-modal="true"]')) return;
  const key = e.key.toLowerCase();
  if (key === 'z' && !e.shiftKey) {
    e.preventDefault();
    undo();
  } else if ((key === 'z' && e.shiftKey) || key === 'y') {
    e.preventDefault();
    redo();
  }
}

const achieved = computed(() => new Set([...feed.kinds, ...visited.value]));

const pulse = computed(() => (props.preview || celebrating.value || step.value >= STEPS.length ? null : `pulse-${STEPS[step.value].goal}`));

watch(view, (v) => {
  if (v !== 'board') visited.value = new Set([...visited.value, v]);
});

watch(
  [achieved, step],
  () => {
    if (props.preview || celebrating.value || step.value >= STEPS.length) return;
    if (achieved.value.has(STEPS[step.value].goal)) {
      celebrating.value = true;
      clearTimeout(timer);
      timer = setTimeout(advance, 1100);
    }
  },
  { deep: true }
);

function advance() {
  clearTimeout(timer);
  celebrating.value = false;
  step.value = Math.min(STEPS.length, step.value + 1);
}

function select(id) {
  activeId.value = id;
  if (view.value === 'compare') view.value = 'board';
}

function setHalf(on) {
  store.updateDetails(roadmap.value.id, { settings: { halfSprints: on } });
}

function reset() {
  clearTimeout(timer);
  store.load();
  resetDemoEvents(store.roadmaps);
  activeId.value = 'demo-q3-platform';
  view.value = 'board';
  visited.value = new Set();
  celebrating.value = false;
  step.value = 0;
  minimised.value = false;
}

function skip() {
  clearTimeout(timer);
  celebrating.value = false;
  step.value = STEPS.length;
  minimised.value = true;
}
</script>

<template>
  <div class="window" :class="[pulse, { preview }]" :inert="preview || undefined" @keydown="onKey">
    <div class="titlebar">
      <span class="dots" aria-hidden="true"><i /><i /><i /></span>
      <span class="address">app.minnimap.com</span>
      <span class="badge">Live demo</span>
    </div>

    <div class="body">
      <aside class="side">
        <img :src="logo" alt="minni map" width="92" class="side-logo" />
        <p class="side-heading">Roadmaps</p>
        <button
          v-for="rm in store.sorted"
          :key="rm.id"
          type="button"
          class="side-item"
          :class="{ active: rm.id === roadmap.id && view !== 'compare' }"
          @click="select(rm.id)"
        >
          <span class="side-name">{{ rm.name }}</span>
          <span class="side-count">{{ rm.projects.length }}</span>
        </button>
        <div class="side-tools">
          <button type="button" class="side-item" :class="{ active: view === 'compare' }" @click="view = 'compare'">
            <Icon name="layers" :size="14" />
            <span class="side-name">Compare and merge</span>
          </button>
        </div>
      </aside>

      <section class="main">
        <header class="head">
          <div>
            <h3>{{ view === 'compare' ? 'Compare roadmaps' : roadmap.name }}</h3>
            <p>{{ summary }}</p>
          </div>
          <div v-if="view !== 'compare'" class="head-actions">
            <AppButton variant="ghost" size="sm" icon="undo" icon-only class="undo-btn" title="Undo (Ctrl+Z)" :disabled="!canUndo" @click="undo">Undo</AppButton>
            <AppButton variant="ghost" size="sm" icon="redo" icon-only title="Redo (Ctrl+Y)" :disabled="!canRedo" @click="redo">Redo</AppButton>
            <AppButton variant="accent" size="sm" icon="plus" @click="openProject(roadmap.id)">Add project</AppButton>
          </div>
        </header>

        <div class="toolbar">
          <Segmented v-model="view" :options="views" />
          <div v-if="view !== 'compare'" class="legend">
            <span v-for="p in roadmap.phases" :key="p.id" class="legend-item" :style="phaseVars(p.color)"><i />{{ p.name }}</span>
          </div>
          <span v-else class="legend" />
          <button
            v-if="view !== 'compare'"
            type="button"
            class="switch"
            role="switch"
            :aria-checked="half"
            @click="setHalf(!half)"
          >
            <span class="track" :class="{ on: half }"><span class="thumb" /></span>
            Half sprints
          </button>
          <button type="button" class="reset" title="Put the demo back how it was" @click="reset">Reset</button>
        </div>

        <div class="content">
          <RoadmapBoard v-if="view === 'board'" :key="`b-${roadmap.id}`" :roadmap="roadmap" />
          <CapacityView v-else-if="view === 'capacity'" :key="`c-${roadmap.id}`" :roadmap="roadmap" />
          <div v-else class="compare">
            <CoverageChart :roadmaps="store.sorted" />
          </div>
        </div>
      </section>
    </div>

    <div v-if="!preview" class="tour">
      <TourCallout
        :steps="STEPS"
        :index="step"
        :celebrating="celebrating"
        :minimised="minimised"
        @next="advance"
        @skip="skip"
        @restart="reset"
        @toggle="minimised = !minimised"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.window {
  position: relative;
  border-radius: 14px;
  background: var(--bg);
  box-shadow: 0 40px 80px -24px rgba(9, 3, 21, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.08);
  overflow: hidden;
  text-align: left;

  &.preview {
    pointer-events: none;
    user-select: none;
  }
}

.titlebar {
  display: flex;
  align-items: center;
  gap: 14px;
  height: 40px;
  padding: 0 14px;
  background: #16102a;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.dots {
  display: flex;
  gap: 7px;

  i {
    width: 11px;
    height: 11px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.16);
  }
}

.address {
  flex: 1;
  max-width: 340px;
  margin: 0 auto;
  height: 24px;
  border-radius: 7px;
  background: rgba(255, 255, 255, 0.07);
  color: rgba(255, 255, 255, 0.55);
  font-size: 12px;
  line-height: 24px;
  text-align: center;
}

.badge {
  font-size: 11px;
  font-weight: 600;
  color: #b9baff;
}

.body {
  display: flex;
  height: 620px;
}

.side {
  width: 196px;
  flex-shrink: 0;
  padding: 18px 10px;
  background: var(--ink);
  color: var(--ink-text);
}

.side-logo {
  display: block;
  margin: 0 8px 22px;
}

.side-heading {
  padding: 0 8px 6px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.36);
}

.side-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 32px;
  padding: 0 8px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: var(--ink-text);
  font-size: 12px;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: var(--ink-2);
    color: #fff;
  }

  &.active {
    background: var(--ink-3);
    color: #fff;
    box-shadow: inset 2px 0 0 var(--iris);
  }
}

.side-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.side-count {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.32);
}

.side-tools {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--ink-line);
}

.main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  padding: 22px 24px 0;
}

.head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  h3 {
    font-size: 21px;
    font-weight: 700;
    letter-spacing: -0.02em;
  }

  p {
    margin-top: 2px;
    font-size: 12px;
    color: var(--text-3);
  }
}

.head-actions {
  display: flex;
  align-items: center;
  gap: 2px;

  .btn--accent {
    margin-left: 6px;
  }
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--line);
}

.legend {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  min-width: 0;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 500;
  color: var(--text-2);

  i {
    width: 8px;
    height: 8px;
    border-radius: 3px;
    background: var(--phase);
  }
}

.switch {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 6px;
  border: 0;
  border-radius: 8px;
  background: none;
  color: var(--text-2);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    color: var(--text);
  }
}

.track {
  position: relative;
  width: 30px;
  height: 17px;
  border-radius: 999px;
  background: var(--line-strong);
  transition: background-color 0.15s;

  &.on {
    background: var(--iris);
  }
}

.thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 13px;
  height: 13px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  transition: transform 0.15s;

  .on & {
    transform: translateX(13px);
  }
}

.reset {
  padding: 4px 6px;
  border: 0;
  background: none;
  color: var(--text-3);
  font-size: 12px;
  cursor: pointer;

  &:hover {
    color: var(--text);
  }
}

.content {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding-top: 4px;
  scrollbar-width: thin;

  :deep(.capacity) {
    padding-top: 12px;
  }
}

.compare {
  padding: 18px 20px;
  margin-top: 12px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: var(--surface);
}

.tour {
  position: absolute;
  right: 20px;
  bottom: 20px;
  z-index: 30;
}

@keyframes ring {
  0% {
    box-shadow: 0 0 0 0 rgba(107, 108, 247, 0.55);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(107, 108, 247, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(107, 108, 247, 0);
  }
}

.pulse-move :deep([data-block='c1']),
.pulse-resize :deep([data-block='c1']),
.pulse-phase :deep([data-block='c1']),
.pulse-undo :deep(.undo-btn),
.pulse-half .switch,
.pulse-capacity :deep(.seg:nth-child(2)),
.pulse-compare :deep(.seg:nth-child(3)) {
  animation: ring 1.6s ease-out infinite;
}
</style>
